import { FC } from "react";
import { TableHeader, TableBody, TableRow } from "@/components/Base/Table";
import { ListViewCard } from "../ListViewCard";
import { GroupHeader } from "./GroupHeader";
import { ResizableTableHeader } from "./ResizableTableHeader";
import { CardGroup, ColumnConfig, SortCriteria } from "../types";
import { Card, Employee, Shop, TagsDocument } from "@/types/v2";

export interface GroupContainerProps {
  group: CardGroup;
  columns: ColumnConfig[];
  showStatistics: boolean;
  showCounts: boolean;
  sortCriteria: SortCriteria[];
  onToggleCollapse: (groupId: string) => void;
  onSort: (field: string, ctrlKey: boolean) => void;
  onColumnWidthChange: (columnId: string, width: number) => void;
  employees?: Employee[];
  shop?: Shop;
  tags?: TagsDocument;
  disabled?: boolean;
  onMoveToClick?: (card: Card) => void;
}

export const GroupContainer: FC<GroupContainerProps> = ({
  group,
  columns,
  showStatistics,
  showCounts,
  sortCriteria,
  onToggleCollapse,
  onSort,
  onColumnWidthChange,
  employees,
  shop,
  tags,
  disabled = false,
  onMoveToClick,
}) => {
  const { cards, isCollapsed } = group;

  return (
    <div className="mb-4 overflow-hidden rounded-lg border border-slate-700 [&_thead]:border-slate-700 [&_tr]:border-slate-700">
      {/* Group Header */}
      <GroupHeader
        group={group}
        showStatistics={showStatistics}
        showCounts={showCounts}
        onToggleCollapse={onToggleCollapse}
      />

      {/* Group Content */}
      {!isCollapsed && cards.length > 0 && (
        <div className="overflow-auto">
          <table className="w-full">
            {/* Column Headers for this group */}
            <TableHeader>
              <TableRow>
                {columns.map((column) => (
                  <ResizableTableHeader
                    key={column.id}
                    column={column}
                    sortCriteria={sortCriteria}
                    onSort={onSort}
                    onWidthChange={onColumnWidthChange}
                  />
                ))}
                {/* Actions column header */}
                <ResizableTableHeader
                  key="actions"
                  column={{
                    id: "actions",
                    field: "actions",
                    label: "Actions",
                    width: 120,
                    minWidth: 120,
                    sortable: false,
                    filterable: false,
                    visible: true,
                    fixed: true,
                    align: "center" as const,
                  }}
                  sortCriteria={sortCriteria}
                  onSort={onSort}
                  onWidthChange={onColumnWidthChange}
                />
              </TableRow>
            </TableHeader>

            {/* Cards in this group */}
            <TableBody>
              {cards.map((card) => (
                <ListViewCard
                  key={card.id}
                  card={card}
                  columns={columns}
                  employees={employees}
                  shop={shop}
                  tags={tags}
                  disabled={disabled}
                  onMoveToClick={onMoveToClick}
                />
              ))}
            </TableBody>
          </table>
        </div>
      )}

      {/* Empty State for collapsed groups */}
      {!isCollapsed && cards.length === 0 && (
        <div className="flex h-20 items-center justify-center text-muted-foreground">
          No cards in this group
        </div>
      )}
    </div>
  );
};
